import { inject, Injectable } from "@angular/core";
import {
  InterpolateFunction,
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  TranslateParser,
  TranslateStore,
} from "@ngx-translate/core";
import { AppSettings } from "../config/app-settings.config";
import { APP_TRANSLATION_TEMPLATE } from "../constants/translate";

@Injectable()
export class AppMissingTranslationHandler implements MissingTranslationHandler {
  protected readonly parser = inject(TranslateParser);
  protected readonly store = inject(TranslateStore);

  private readonly namespaceMap = new Map<string, readonly string[]>();

  public handle({
    key: translateKey,
    translateService: translate,
    interpolateParams,
  }: MissingTranslationHandlerParams): ReturnType<MissingTranslationHandler["handle"]> {
    if (!translateKey) {
      return "";
    }

    try {
      let namespace = this.namespaceMap.get(translateKey);

      if (!namespace) {
        const keys = translateKey.split(".");
        this.namespaceMap.set(translateKey, keys);
        namespace = keys;
      }

      const translationList: unknown[] = [];

      const defaultLanguage = AppSettings.defaultLanguage;
      if (defaultLanguage && translate.getCurrentLang() !== defaultLanguage) {
        const defaultLangTranslations: unknown = this.store.getTranslations(defaultLanguage);
        if (defaultLangTranslations) {
          translationList.push(defaultLangTranslations);
        }
      }

      const templateLangTranslations: unknown = this.store.getTranslations(APP_TRANSLATION_TEMPLATE);
      if (templateLangTranslations) {
        translationList.push(templateLangTranslations);
      }

      let result: unknown = translateKey;

      for (const translation of translationList) {
        result = this.getTranslationValue(namespace, translation) ?? translateKey;
        if (result !== translateKey) {
          return this.tryInterpolate(result, interpolateParams)?.toString();
        }
      }
    } catch (err: unknown) {
      console.error(err);
    }

    return translateKey;
  }

  private getTranslationValue(namespace: readonly string[], translation: unknown): unknown {
    try {
      for (const key of namespace) {
        if (translation && typeof translation === "object") {
          if (key in translation) {
            translation = translation[<keyof object>key];
          } else {
            return undefined;
          }
        }
        if (translation === undefined || translation === null) {
          return undefined;
        }
      }
      return translation ?? undefined;
    } catch (err: unknown) {
      console.error(err);
      return undefined;
    }
  }

  private tryInterpolate(translation: unknown, interpolateParams: object | undefined): unknown {
    try {
      return interpolateParams && (typeof translation === "string" || typeof translation === "function")
        ? this.parser.interpolate(<string | InterpolateFunction>translation, interpolateParams)
        : translation;
    } catch (err: unknown) {
      console.error(err);
      return translation;
    }
  }
}
